// useUsers — fetches /users with the current filters, exposes refetch().
//
// Phase 0 keeps it deliberately simple: re-fetch on filter change, no
// SWR-style cache, no optimistic updates. Inline refetch() after any
// mutation (invite, patch, suspend, ...) is fine for an internal-team-size
// list (well under 100 rows).

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListUsersQuery, ListUsersResponse } from '@gc-coach/shared';
import { listUsers } from '../api/endpoints.js';

export interface UseUsersResult {
  data: ListUsersResponse | null;
  isLoading: boolean;
  /** Last fetch error code, or null. */
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useUsers(query: ListUsersQuery): UseUsersResult {
  const [data, setData] = useState<ListUsersResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Stable query key so we only refetch when filters actually change.
  const key = useMemo(
    () => `${query.status ?? ''}|${query.role ?? ''}|${query.q?.trim() ?? ''}`,
    [query.status, query.role, query.q],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listUsers(query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // We intentionally don't depend on `query` itself — its identity churns
    // every render. The serialized `key` deps the outer effect instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
